// src/pages/ExpensesPage.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Grid, TextField, Select, MenuItem, InputLabel, FormControl, Switch, FormControlLabel, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import api from '../api/index.js';
import { format } from 'date-fns';

const ExpensesPage = () => {
    const [expenses, setExpenses] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        store_id: '', description: '', amount: '',
        is_scheduled: false, next_due_date: format(new Date(), 'yyyy-MM-dd'),
        is_recurring: false, recurrence_period: 'MONTHLY'
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [expensesRes, storesRes] = await Promise.all([
                api.get('/expenses'),
                api.get('/stores')
            ]);
            setExpenses(expensesRes.data);
            setStores(storesRes.data);
        } catch (err) { setError('No se pudieron cargar los datos.'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async () => {
        try {
            await api.post('/expenses', formData);
            alert('Gasto guardado.');
            fetchData(); // Recargar
            // Reset form
            setFormData({
                store_id: '', description: '', amount: '',
                is_scheduled: false, next_due_date: format(new Date(), 'yyyy-MM-dd'),
                is_recurring: false, recurrence_period: 'MONTHLY'
            });
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar el gasto.');
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'description', headerName: 'Descripción', flex: 1 },
        { field: 'store_name', headerName: 'Tienda', width: 180 },
        { field: 'amount', headerName: 'Monto', type: 'number', valueFormatter: p => `$${Number(p.value).toFixed(2)}` },
        { 
        field: 'execution_date', 
        headerName: 'Fecha de Pago', 
        width: 150,
        // --- CORRECCIÓN AQUÍ ---
        // 'value' es el valor crudo de la celda (puede ser null)
        valueGetter: (value) => value ? new Date(value).toLocaleDateString() : 'Pendiente'
    },
      { 
        field: 'next_due_date', 
        headerName: 'Próx. Vencimiento', 
        width: 150, 
        // --- CORRECCIÓN AQUÍ ---
        valueGetter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    },
        { field: 'is_recurring', headerName: 'Recurrente', type: 'boolean' },
    ];

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Gestión de Gastos</Typography>
            
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6">Registrar o Programar Gasto</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Descripción" name="description" value={formData.description} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="Monto" name="amount" type="number" value={formData.amount} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={3}><FormControl fullWidth>
                        <InputLabel>Tienda</InputLabel>
                        <Select name="store_id" value={formData.store_id} label="Tienda" onChange={handleInputChange}>
                            {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                        </Select>
                    </FormControl></Grid>
                    
                    <Grid item xs={12}><FormControlLabel control={<Switch checked={formData.is_scheduled} onChange={handleInputChange} name="is_scheduled" />} label="Programar este gasto para el futuro" /></Grid>
                    
                    {formData.is_scheduled && <>
                        <Grid item xs={12} sm={4}><TextField label="Fecha de Vencimiento" name="next_due_date" type="date" value={formData.next_due_date} onChange={handleInputChange} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={4}><FormControlLabel control={<Switch checked={formData.is_recurring} onChange={handleInputChange} name="is_recurring" />} label="Es Recurrente" /></Grid>
                        {formData.is_recurring && <Grid item xs={12} sm={4}><FormControl fullWidth>
                            <InputLabel>Período</InputLabel>
                            <Select name="recurrence_period" value={formData.recurrence_period} label="Período" onChange={handleInputChange}>
                                <MenuItem value="DAILY">Diario</MenuItem>
                                <MenuItem value="WEEKLY">Semanal</MenuItem>
                                <MenuItem value="MONTHLY">Mensual</MenuItem>
                                <MenuItem value="YEARLY">Anual</MenuItem>
                            </Select>
                        </FormControl></Grid>}
                    </>}

                    <Grid item xs={12}><Button variant="contained" onClick={handleSubmit}>{formData.is_scheduled ? 'Programar Gasto' : 'Registrar Gasto Realizado'}</Button></Grid>
                </Grid>
            </Paper>

            <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid rows={expenses} columns={columns} loading={loading} />
            </Box>
        </Box>
    );
};

export default ExpensesPage;